const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://staging-mme.digitaloasis.xyz:8085';

// Utility function to convert relative URLs to absolute URLs
export const getAbsoluteUrl = (relativeUrl?: string): string | undefined => {
  if (!relativeUrl) return undefined;

  // If it's already an absolute URL, return as is
  if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
    return relativeUrl;
  }

  // If it's a relative URL, prepend the API base URL
  if (relativeUrl.startsWith('/')) {
    return `${API_BASE_URL}${relativeUrl}`;
  }

  // If it doesn't start with /, assume it's relative to the API base
  return `${API_BASE_URL}/${relativeUrl}`;
};

export interface UserSubscription {
  id: number;
  userId: number;
  productId: number;
  productName: string;
  productSlug: string;
  productDescription: string;
  productDurationBilling: number;
  productBillingCycle: BillingCycle;
  currentPrice: number;
  status: 'TRIAL' | 'ACTIVE' | 'CANCELLED' | 'ONGOING' | 'EXPIRED' | 'INACTIVE';
  startedAt: string;
  endedAt?: string;
  createdAt: string;
  updatedAt: string;
  product?: Product;
  features?: Array<{
    id: number;
    featureId: number;
    featureCode: ProductFeatureCode;
    featureName: string;
    featureDescription: string;
    value: string;
    feature?: ProductFeature;
  }>;
}

export interface User {
  id: number;
  email: string;
  username?: string;
  name?: string;
  phone?: string;
  avatar?: string;
  isActive: boolean;
  isDeleted: boolean;
  isVerified: boolean;
  dateOfBirth?: string;
  gender?: 'MALE' | 'FEMALE' | 'OTHER';
  city?: string;
  socialProvider?: 'FACEBOOK' | 'GOOGLE' | 'APPLE';
  socialId?: string;
  socialEmail?: string;
  subscriptions?: UserSubscription[]; // User's active subscriptions
  createdAt: string;
  updatedAt: string;
}

export interface Admin {
  id: number;
  email: string;
  name: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'STAFF';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastLogin?: string;
}

export interface PaginatedResponse<T> {
  users?: T[];
  admins?: T[];
  teams?: T[];
  banners?: T[];
  news?: T[];
  matches?: T[];
  standings?: T[];
  ticketTypes?: T[];
  matchTickets?: T[];
  subscriptions?: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface FindAllUsersParams {
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: 'id' | 'name' | 'email' | 'createdAt' | 'lastLogin';
  sortOrder?: 'asc' | 'desc';
  isActive?: boolean;
  subscriptionId?: string;
}

export interface FindAllAdminsParams {
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: 'id' | 'name' | 'email' | 'role' | 'createdAt' | 'lastLogin';
  sortOrder?: 'asc' | 'desc';
  isActive?: boolean;
  role?: string;
}

export interface Banner {
  id: number;
  title: string;
  imageUrl?: string;
  linkUrl?: string;
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export enum Duration {
  ONE_MONTH = 'ONE_MONTH',
  FULL = 'FULL',
}



export enum CompetitivenessData {
  ONE_PLATFORM = 'ONE_PLATFORM',
  THREE_PLATFORM = 'THREE_PLATFORM',
  ALL_PLATFORM = 'ALL_PLATFORM',
}

export enum AdditionalFeatures {
  IMOLA_MEMORY = 'IMOLA_MEMORY',
}

export enum BillingCycle {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

export enum ProductFeatureCode {
  CATEGORY_STORE = 'CATEGORY_STORE',
  MAX_CONNECT_STORE = 'MAX_CONNECT_STORE',
  INSIGHT = 'INSIGHT',
  PATHSIGHT = 'PATHSIGHT',
  FORESIGHT = 'FORESIGHT',
  CONVO = 'CONVO',
  COMPETITIVENESS_DATA = 'COMPETITIVENESS_DATA',
  IMOLA_MEMORY = 'IMOLA_MEMORY',
}

export interface Subscription {
  id: number;
  userId: number;
  productId: number;
  productName: string;
  productSlug: string;
  productDescription: string;
  productDurationBilling: number;
  productBillingCycle: BillingCycle;
  currentPrice: number;
  status: 'ACTIVE' | 'CANCELLED' | 'ONGOING' | 'EXPIRED' | 'INACTIVE';
  isTrial: boolean;
  startedAt: string;
  endedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  features?: Array<{
    id: number;
    featureId: number;
    featureCode: ProductFeatureCode;
    featureName: string;
    featureDescription: string;
    value: string;
    feature?: ProductFeature;
  }>;
  user?: {
    id: number;
    email: string;
    name?: string;
  };
  product?: {
    id: number;
    name: string;
    slug: string;
    basePrice: number;
  };
}

export interface SubscriptionPayload {
  userId: number;
  productId: number;
  // Optional marketing fields provided by CMS when creating/updating subscriptions
  // Backend will resolve these codes to IDs (affiliateMemberId / promoId) as needed.
  affiliateCode?: string;
  promoCode?: string;
  currentPrice?: number;
  isTrial?: boolean;
  status?: 'ACTIVE' | 'CANCELLED' | 'ONGOING' | 'EXPIRED' | 'INACTIVE';
  startedAt?: string;
  endedAt?: string;
  features?: Array<{
    featureId: number;
    value: string;
  }>;
}

export interface ProductFeatureValue {
  id: number;
  featureId: number;
  value: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProductFeature {
  id: number;
  code: string;
  unit?: string | null;
  name: string;
  description: string;
  softDelete: boolean;
  createdAt: string;
  updatedAt: string;
  values?: ProductFeatureValue[];
}

export interface ProductFeatureLimit {
  id: number;
  productId: number;
  featureId: number;
  value: string;
  note?: string;
  feature?: ProductFeature;
}

export interface ProductFeatureLimitPayload {
  featureId: number;
  value: string;
  note?: string;
}

export interface ProductPricingHistory {
  id: number;
  productId: number;
  price: number;
  startDate: string;
  endDate?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProductPricingHistoryPayload {
  price: number;
  startDate: string;
  endDate?: string;
}

export interface Product {
  id: number;
  name: string;
  slug: string;
  description: string;
  basePrice: number;
  durationBilling: number;
  billingCycle: BillingCycle;
  isActive: boolean;
  softDelete: boolean;
  createdAt: string;
  updatedAt: string;
  featureLimits?: ProductFeatureLimit[];
  pricingHistory?: ProductPricingHistory[];
}

export interface ProductPayload {
  name: string;
  slug: string;
  description?: string;
  basePrice: number;
  durationBilling: number;
  billingCycle: BillingCycle;
  isActive?: boolean;
  featureLimits?: ProductFeatureLimitPayload[];
  pricingHistory?: ProductPricingHistoryPayload[];
}

export interface ProductFeatureValuePayload {
  value: string;
}

export interface ProductFeaturePayload {
  code: string;
  name: string;
  description?: string;
  unit?: string;
  values?: ProductFeatureValuePayload[];
}

export interface FindAllProductsParams {
  search?: string;
  billingCycle?: BillingCycle;
  isActive?: string;
  includeDeleted?: string;
  page?: number;
  limit?: number;
}

export interface ProductListResponse {
  products: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface FindAllProductFeaturesParams {
  search?: string;
  code?: ProductFeatureCode;
  includeDeleted?: string;
  page?: number;
  limit?: number;
}

export interface ProductFeatureListResponse {
  features: ProductFeature[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface FindAllSubscriptionsParams {
  search?: string;
  userId?: number;
  productId?: number;
  status?: 'TRIAL' | 'ACTIVE' | 'CANCELLED' | 'ONGOING' | 'EXPIRED' | 'INACTIVE';
  page?: number;
  limit?: number;
}

export interface SubscriptionListResponse {
  subscriptions: Subscription[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Affiliate Member interfaces
export enum AffiliateMemberStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  BANNED = 'BANNED',
}

export interface AffiliateMember {
  id: number;
  userId?: number;
  code: string;
  slug: string;
  walletBalance: number;
  paymentMethodId?: number;
  accountNumber?: string;
  status: AffiliateMemberStatus;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    email: string;
    name?: string;
  };
}

export interface AffiliateMemberPayload {
  userId?: number;
  walletBalance?: number;
  status?: AffiliateMemberStatus;
  paymentMethodId?: number;
  accountNumber?: string;
}

export interface FindAllAffiliateMembersParams {
  search?: string;
  userId?: number;
  status?: AffiliateMemberStatus;
  page?: number;
  limit?: number;
}

export interface AffiliateMemberListResponse {
  members: AffiliateMember[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Affiliate Program interfaces
export interface AffiliateProgramProduct {
  id: number;
  productId: number;
  product?: {
    id: number;
    name: string;
    slug: string;
  };
}

export interface AffiliateProgram {
  id: number;
  name: string;
  commissionType: string; // 'percentage' or 'fixed'
  commissionValue: number;
  startDate: string;
  endDate?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  products?: AffiliateProgramProduct[];
}

export interface AffiliateProgramProductInput {
  productId: number;
}

export interface AffiliateProgramPayload {
  name: string;
  commissionType: string;
  commissionValue: number;
  startDate: string;
  endDate?: string;
  isActive?: boolean;
  products?: AffiliateProgramProductInput[];
}

export interface FindAllAffiliateProgramsParams {
  search?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface AffiliateProgramListResponse {
  programs: AffiliateProgram[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Affiliate Commission interfaces
export enum AffiliateCommissionStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  PAID = 'PAID',
  VOID = 'VOID',
}

export interface AffiliateCommission {
  id: number;
  affiliateMemberId: number;
  programId: number;
  orderId: number;
  commissionAmount: number;
  status: AffiliateCommissionStatus;
  createdAt: string;
  confirmedAt?: string | null;
  paidAt?: string | null;
  affiliateMember?: {
    id: number;
    code: string;
    slug: string;
    userId?: number;
    paymentMethodId?: number;
    accountNumber?: string;
  };
  program?: {
    id: number;
    name: string;
    commissionType?: string;
  };
  order?: {
    id: number;
    orderNumber: string;
    totalAmount: number;
    userId?: number;
    productId?: number;
    product?: {
      id: number;
      name: string;
      slug: string;
    };
  };
}

export interface AffiliateCommissionPayload {
  affiliateMemberId: number;
  programId: number;
  orderId: number;
  commissionAmount: number;
  status?: AffiliateCommissionStatus;
  confirmedAt?: string;
  paidAt?: string;
}

export interface FindAllAffiliateCommissionsParams {
  affiliateMemberId?: number;
  programId?: number;
  orderId?: number;
  status?: AffiliateCommissionStatus;
  page?: number;
  limit?: number;
}

export interface AffiliateCommissionListResponse {
  commissions: AffiliateCommission[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AffiliateCommissionByMemberResponse {
  commissions: AffiliateCommission[];
  summary: {
    totalEarned: number;
    totalPending: number;
    totalConfirmed: number;
  };
}

export interface AffiliateAdminNote {
  id: number;
  affiliateMemberId: number;
  adminId: number;
  note: string;
  createdAt: string;
  updatedAt: string;
  affiliateMember?: {
    id: number;
    code: string;
    slug: string;
    userId?: number;
    status: AffiliateMemberStatus;
    paymentMethodId?: number;
    accountNumber?: string;
  };
  admin?: {
    id: number;
    name: string;
    email: string;
    role: 'SUPER_ADMIN' | 'ADMIN' | 'STAFF';
  };
}

export interface CreateAffiliateAdminNotePayload {
  affiliateMemberId: number;
  adminId: number;
  note: string;
}

export enum AffiliateWithdrawalStatus {
  REQUESTED = 'REQUESTED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED',
}

export interface AffiliateWithdrawal {
  id: number;
  affiliateMemberId: number;
  amount: number;
  status: AffiliateWithdrawalStatus;
  payoutMethod?: string | null;
  requestedAt?: string | null;
  approvedAt?: string | null;
  paidAt?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  affiliateMember?: {
    id: number;
    code: string;
    slug: string;
    userId?: number;
    walletBalance: number;
    status: AffiliateMemberStatus;
  };
}

export interface AffiliateWithdrawalResponse {
  withdrawals: AffiliateWithdrawal[];
  totalPaidOut: number | null;
}


// Subscription Order interfaces
export interface SubscriptionOrder {
  id: number;
  subscriptionId: number;
  userId: number;
  productId: number;
  orderNumber: string;
  subtotalAmount: number;
  discountAmount: number;
  totalAmount: number;
  currency: string;
  periodStart: string;
  periodEnd: string;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED' | 'EXPIRED';
  promoId?: number | null;
  affiliateMemberId?: number | null;
  createdAt: string;
  paidAt?: string | null;
}

export interface FindAllSubscriptionOrdersParams {
  search?: string;
  userId?: number;
  subscriptionId?: number;
  productId?: number;
  status?: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED' | 'EXPIRED';
  page?: number;
  limit?: number;
}

export interface SubscriptionOrderListResponse {
  orders: SubscriptionOrder[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Promo Code interfaces
export interface PromoCodeProduct {
  id: number;
  productId: number;
  product?: {
    id: number;
    name: string;
    slug: string;
  };
}

export interface PromoCode {
  id: number;
  code: string;
  name: string;
  description: string;
  discountType: string; // 'percentage' or 'fixed'
  discountValue: number;
  maxDiscount?: number | null;
  minPurchase?: number | null;
  startDate: string;
  endDate?: string | null;
  usageLimit?: number | null;
  perUserLimit?: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  products?: PromoCodeProduct[];
}

export interface PromoCodeProductInput {
  productId: number;
}

export interface PromoCodePayload {
  code: string;
  name: string;
  description: string;
  discountType: string;
  discountValue: number;
  maxDiscount?: number;
  minPurchase?: number;
  startDate: string;
  endDate?: string;
  usageLimit?: number;
  perUserLimit?: number;
  isActive?: boolean;
  products?: PromoCodeProductInput[];
}

export interface FindAllPromoCodesParams {
  search?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface PromoCodeListResponse {
  promoCodes: PromoCode[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Payment Provider interfaces
export interface PaymentProviderCredential {
  id: number;
  providerId: number;
  name: string;
  environment: string;
  merchantId: string;
  serverKey: string;
  clientKey: string;
  additionalConfig?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentProvider {
  id: number;
  code: string;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  credentials?: PaymentProviderCredential[];
}

export interface PaymentProviderCredentialPayload {
  name: string;
  environment: string;
  merchantId: string;
  serverKey: string;
  clientKey: string;
  additionalConfig?: string;
  isActive?: boolean;
}

export interface PaymentProviderPayload {
  code: string;
  name: string;
  description: string;
  isActive?: boolean;
  credentials?: PaymentProviderCredentialPayload[];
}

export interface FindAllPaymentProvidersParams {
  search?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface PaymentProviderListResponse {
  providers: PaymentProvider[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface FindAllBannersParams {
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: 'id' | 'title' | 'order' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  isActive?: string;
}

export interface PaymentMethod {
  id: number;
  name: string;
  type: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

class ApiService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private getAuthHeadersWithoutContentType(): HeadersInit {
    const token = localStorage.getItem('token');
    return {
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      // Handle 401 Unauthorized - automatically logout
      if (response.status === 401) {
        logout();
        throw new Error('Session expired. Please login again.');
      }

      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async getUserCounts(startDate?: string, endDate?: string): Promise<{
    totalUserPeriod: number;
    totalUserPeriodPercentage: number;
    description: string;
  }> {
    const searchParams = new URLSearchParams();
    if (startDate) searchParams.append('startDate', startDate);
    if (endDate) searchParams.append('endDate', endDate);

    const url = `${API_BASE_URL}/users/analytics${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<{
      totalUserPeriod: number;
      totalUserPeriodPercentage: number;
      description: string;
    }>(response);
  }

  async getUserChartData(startDate?: string, endDate?: string): Promise<Array<{
    date?: string;
    month?: string;
    users: number;
    newUsers: number;
  }>> {
    const searchParams = new URLSearchParams();
    if (startDate) searchParams.append('startDate', startDate);
    if (endDate) searchParams.append('endDate', endDate);

    const url = `${API_BASE_URL}/users/analytics/chart${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<Array<{
      date?: string;
      month?: string;
      users: number;
      newUsers: number;
    }>>(response);
  }

  async getUsers(params: FindAllUsersParams = {}): Promise<PaginatedResponse<User>> {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const url = `${API_BASE_URL}/users${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<PaginatedResponse<User>>(response);
  }

  async getUser(id: number): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<User>(response);
  }

  async updateUser(id: number, userData: Partial<User>): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(userData),
    });

    return this.handleResponse<User>(response);
  }

  async toggleUserStatus(id: number): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/${id}/toggle-status`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<User>(response);
  }

  async deleteUser(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
  }

  async updateUserSubscription(userId: number, productId: number): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/${userId}/subscription`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ productId }),
    });

    return this.handleResponse<User>(response);
  }

  // Admins API
  async getAdmins(params: FindAllAdminsParams = {}): Promise<PaginatedResponse<Admin>> {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const url = `${API_BASE_URL}/admins${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<PaginatedResponse<Admin>>(response);
  }

  async getAdmin(id: number): Promise<Admin> {
    const response = await fetch(`${API_BASE_URL}/admins/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<Admin>(response);
  }

  async createAdmin(adminData: { email: string; name: string; password: string; role: 'SUPER_ADMIN' | 'ADMIN' | 'STAFF' }): Promise<Admin> {
    const response = await fetch(`${API_BASE_URL}/admins`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(adminData),
    });

    return this.handleResponse<Admin>(response);
  }

  async updateAdmin(id: number, adminData: Partial<Admin>): Promise<Admin> {
    const response = await fetch(`${API_BASE_URL}/admins/${id}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(adminData),
    });

    return this.handleResponse<Admin>(response);
  }

  async toggleAdminStatus(id: number): Promise<Admin> {
    const response = await fetch(`${API_BASE_URL}/admins/${id}/toggle-status`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<Admin>(response);
  }

  async deleteAdmin(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admins/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
  }

  // Banners API
  async getBanners(params: FindAllBannersParams = {}): Promise<PaginatedResponse<Banner>> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });
    const url = `${API_BASE_URL}/banners${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<PaginatedResponse<Banner>>(response);
  }

  async getBanner(id: number): Promise<Banner> {
    const response = await fetch(`${API_BASE_URL}/banners/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<Banner>(response);
  }

  async createBanner(bannerData: Partial<Banner>, imageFile?: File): Promise<Banner> {
    const formData = new FormData();
    Object.entries(bannerData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });
    if (imageFile) {
      formData.append('image', imageFile);
    }
    const response = await fetch(`${API_BASE_URL}/banners`, {
      method: 'POST',
      headers: this.getAuthHeadersWithoutContentType(),
      body: formData,
    });
    return this.handleResponse<Banner>(response);
  }

  async updateBanner(id: number, bannerData: Partial<Banner>, imageFile?: File): Promise<Banner> {
    const formData = new FormData();
    Object.entries(bannerData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });
    if (imageFile) {
      formData.append('image', imageFile);
    }
    const response = await fetch(`${API_BASE_URL}/banners/${id}`, {
      method: 'PATCH',
      headers: this.getAuthHeadersWithoutContentType(),
      body: formData,
    });
    return this.handleResponse<Banner>(response);
  }

  async deleteBanner(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/banners/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
  }

  async requestDeleteAccount(data: { email?: string; phone?: string }): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE_URL}/mobile/users/request-delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to request account deletion');
    }
    return response.json();
  }

  // Subscriptions API
  async getSubscriptions(params: FindAllSubscriptionsParams = {}): Promise<SubscriptionListResponse> {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const url = `${API_BASE_URL}/subscriptions${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<SubscriptionListResponse>(response);
  }

  async getSubscription(id: number): Promise<Subscription> {
    const response = await fetch(`${API_BASE_URL}/subscriptions/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<Subscription>(response);
  }

  async getProductCountByUser(startDate?: string, endDate?: string): Promise<{
    [key: string]: number;
  }> {
    const searchParams = new URLSearchParams();
    if (startDate) searchParams.append('startDate', startDate);
    if (endDate) searchParams.append('endDate', endDate);

    const url = `${API_BASE_URL}/subscriptions/analytics/products${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<{
      [key: string]: number;
    }>(response);
  }

  async createSubscription(payload: SubscriptionPayload): Promise<Subscription> {
    const response = await fetch(`${API_BASE_URL}/subscriptions`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<Subscription>(response);
  }

  async updateSubscription(id: number, payload: Partial<SubscriptionPayload>): Promise<Subscription> {
    const response = await fetch(`${API_BASE_URL}/subscriptions/${id}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<Subscription>(response);
  }

  async deleteSubscription(id: number): Promise<Subscription> {
    const response = await fetch(`${API_BASE_URL}/subscriptions/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<Subscription>(response);
  }

  async restoreSubscription(id: number): Promise<Subscription> {
    const response = await fetch(`${API_BASE_URL}/subscriptions/${id}/restore`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<Subscription>(response);
  }

  // Auth API
  async login(email: string, password: string): Promise<{ access_token: string; user: any }> {
    const response = await fetch(`${API_BASE_URL}/auth/admin/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async requestForgotPasswordOtp(email: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/admin/forgot-password/request-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    return this.handleResponse<{ message: string }>(response);
  }

  async verifyForgotPasswordOtp(email: string, otp: string): Promise<{ resetToken: string; message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/admin/forgot-password/verify-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, otp }),
    });

    return this.handleResponse<{ resetToken: string; message: string }>(response);
  }

  async resetPassword(email: string, newPassword: string, resetToken: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/admin/forgot-password/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, newPassword, resetToken }),
    });

    return this.handleResponse<{ message: string }>(response);
  }

  // Products API
  async getProducts(params: FindAllProductsParams = {}): Promise<ProductListResponse> {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const url = `${API_BASE_URL}/products${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<ProductListResponse>(response);
  }

  async getProduct(id: number): Promise<Product> {
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<Product>(response);
  }

  async createProduct(payload: ProductPayload): Promise<Product> {
    const response = await fetch(`${API_BASE_URL}/products`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<Product>(response);
  }

  async updateProduct(id: number, payload: Partial<ProductPayload>): Promise<Product> {
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<Product>(response);
  }

  async deleteProduct(id: number): Promise<Product> {
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<Product>(response);
  }

  async restoreProduct(id: number): Promise<Product> {
    const response = await fetch(`${API_BASE_URL}/products/${id}/restore`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<Product>(response);
  }

  // Product Features API
  async getProductFeatures(
    params: FindAllProductFeaturesParams = {},
  ): Promise<ProductFeatureListResponse> {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const url = `${API_BASE_URL}/product-features${searchParams.toString() ? `?${searchParams.toString()}` : ''
      }`;

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<ProductFeatureListResponse>(response);
  }

  async createProductFeature(payload: ProductFeaturePayload): Promise<ProductFeature> {
    const response = await fetch(`${API_BASE_URL}/product-features`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<ProductFeature>(response);
  }

  async updateProductFeature(
    id: number,
    payload: Partial<ProductFeaturePayload>,
  ): Promise<ProductFeature> {
    const response = await fetch(`${API_BASE_URL}/product-features/${id}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<ProductFeature>(response);
  }

  async deleteProductFeature(id: number): Promise<ProductFeature> {
    const response = await fetch(`${API_BASE_URL}/product-features/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<ProductFeature>(response);
  }

  async restoreProductFeature(id: number): Promise<ProductFeature> {
    const response = await fetch(`${API_BASE_URL}/product-features/${id}/restore`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<ProductFeature>(response);
  }

  // Affiliate Members API
  async getAffiliateMembers(params: FindAllAffiliateMembersParams = {}): Promise<AffiliateMemberListResponse> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });

    const response = await fetch(`${API_BASE_URL}/affiliate-members?${searchParams}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<AffiliateMemberListResponse>(response);
  }

  async getAffiliateMember(id: number): Promise<AffiliateMember> {
    const response = await fetch(`${API_BASE_URL}/affiliate-members/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<AffiliateMember>(response);
  }

  async createAffiliateMember(payload: AffiliateMemberPayload): Promise<AffiliateMember> {
    const response = await fetch(`${API_BASE_URL}/affiliate-members`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<AffiliateMember>(response);
  }

  async updateAffiliateMember(id: number, payload: Partial<AffiliateMemberPayload>): Promise<AffiliateMember> {
    const response = await fetch(`${API_BASE_URL}/affiliate-members/${id}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<AffiliateMember>(response);
  }

  async deleteAffiliateMember(id: number): Promise<AffiliateMember> {
    const response = await fetch(`${API_BASE_URL}/affiliate-members/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<AffiliateMember>(response);
  }

  // Affiliate Programs API
  async getAffiliatePrograms(params: FindAllAffiliateProgramsParams = {}): Promise<AffiliateProgramListResponse> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });

    const response = await fetch(`${API_BASE_URL}/affiliate-programs?${searchParams}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<AffiliateProgramListResponse>(response);
  }

  async getAffiliateProgram(id: number): Promise<AffiliateProgram> {
    const response = await fetch(`${API_BASE_URL}/affiliate-programs/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<AffiliateProgram>(response);
  }

  async createAffiliateProgram(payload: AffiliateProgramPayload): Promise<AffiliateProgram> {
    const response = await fetch(`${API_BASE_URL}/affiliate-programs`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<AffiliateProgram>(response);
  }

  async updateAffiliateProgram(id: number, payload: Partial<AffiliateProgramPayload>): Promise<AffiliateProgram> {
    const response = await fetch(`${API_BASE_URL}/affiliate-programs/${id}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<AffiliateProgram>(response);
  }

  async deleteAffiliateProgram(id: number): Promise<AffiliateProgram> {
    const response = await fetch(`${API_BASE_URL}/affiliate-programs/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<AffiliateProgram>(response);
  }

  // Affiliate Commissions API
  async getAffiliateCommissions(params: FindAllAffiliateCommissionsParams = {}): Promise<AffiliateCommissionListResponse> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });

    const response = await fetch(`${API_BASE_URL}/affiliate-commissions?${searchParams}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<AffiliateCommissionListResponse>(response);
  }

  async getAffiliateCommission(id: number): Promise<AffiliateCommission> {
    const response = await fetch(`${API_BASE_URL}/affiliate-commissions/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<AffiliateCommission>(response);
  }

  async createAffiliateCommission(payload: AffiliateCommissionPayload): Promise<AffiliateCommission> {
    const response = await fetch(`${API_BASE_URL}/affiliate-commissions`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<AffiliateCommission>(response);
  }

  async updateAffiliateCommission(id: number, payload: Partial<AffiliateCommissionPayload>): Promise<AffiliateCommission> {
    const response = await fetch(`${API_BASE_URL}/affiliate-commissions/${id}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<AffiliateCommission>(response);
  }

  async deleteAffiliateCommission(id: number): Promise<AffiliateCommission> {
    const response = await fetch(`${API_BASE_URL}/affiliate-commissions/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<AffiliateCommission>(response);
  }

  async getAffiliateCommissionsByMemberId(affiliateMemberId: number): Promise<AffiliateCommissionByMemberResponse> {
    const response = await fetch(`${API_BASE_URL}/affiliate-commissions/by-affiliate-member/${affiliateMemberId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<AffiliateCommissionByMemberResponse>(response);
  }

  // Affiliate Admin Notes API
  async getAffiliateAdminNotesByMemberId(affiliateMemberId: number): Promise<AffiliateAdminNote[]> {
    const response = await fetch(`${API_BASE_URL}/affiliate-admin-notes/by-affiliate-member/${affiliateMemberId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<AffiliateAdminNote[]>(response);
  }

  async createAffiliateAdminNote(payload: CreateAffiliateAdminNotePayload): Promise<AffiliateAdminNote> {
    const response = await fetch(`${API_BASE_URL}/affiliate-admin-notes`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<AffiliateAdminNote>(response);
  }

  // Affiliate Withdrawals API
  async getAffiliateWithdrawalsByMemberId(affiliateMemberId: number): Promise<AffiliateWithdrawalResponse> {
    const response = await fetch(`${API_BASE_URL}/affiliate-withdrawals/by-affiliate-member/${affiliateMemberId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<AffiliateWithdrawalResponse>(response);
  }

  // Subscription Orders API
  async getSubscriptionOrders(params: FindAllSubscriptionOrdersParams = {}): Promise<SubscriptionOrderListResponse> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });

    const response = await fetch(`${API_BASE_URL}/subscription-orders?${searchParams}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<SubscriptionOrderListResponse>(response);
  }

  async getTotalSubscriptionsByProduct(startDate?: string, endDate?: string): Promise<{
    [key: string]: {
      productName: string;
      productSlug: string;
      total: number;
    };
  }> {
    const searchParams = new URLSearchParams();
    if (startDate) searchParams.append('startDate', startDate);
    if (endDate) searchParams.append('endDate', endDate);

    const url = `${API_BASE_URL}/subscription-orders/analytics/products${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<{
      [key: string]: {
        productName: string;
        productSlug: string;
        total: number;
      };
    }>(response);
  }

  async getOrderStatisticsByStatus(startDate?: string, endDate?: string): Promise<{
    PENDING?: { 
      count: number; 
      totalSubtotalAmount: number;
      countPercentage: number;
      amountPercentage: number;
      description: string;
    };
    PAID?: { 
      count: number; 
      totalSubtotalAmount: number;
      countPercentage: number;
      amountPercentage: number;
      description: string;
    };
    FAILED?: { 
      count: number; 
      totalSubtotalAmount: number;
      countPercentage: number;
      amountPercentage: number;
      description: string;
    };
    REFUNDED?: { 
      count: number; 
      totalSubtotalAmount: number;
      countPercentage: number;
      amountPercentage: number;
      description: string;
    };
    EXPIRED?: { 
      count: number; 
      totalSubtotalAmount: number;
      countPercentage: number;
      amountPercentage: number;
      description: string;
    };
  }> {
    const searchParams = new URLSearchParams();
    if (startDate) searchParams.append('startDate', startDate);
    if (endDate) searchParams.append('endDate', endDate);

    const url = `${API_BASE_URL}/subscription-orders/analytics/status${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<{
      PENDING?: { 
        count: number; 
        totalSubtotalAmount: number;
        countPercentage: number;
        amountPercentage: number;
        description: string;
      };
      PAID?: { 
        count: number; 
        totalSubtotalAmount: number;
        countPercentage: number;
        amountPercentage: number;
        description: string;
      };
      FAILED?: { 
        count: number; 
        totalSubtotalAmount: number;
        countPercentage: number;
        amountPercentage: number;
        description: string;
      };
      REFUNDED?: { 
        count: number; 
        totalSubtotalAmount: number;
        countPercentage: number;
        amountPercentage: number;
        description: string;
      };
      EXPIRED?: { 
        count: number; 
        totalSubtotalAmount: number;
        countPercentage: number;
        amountPercentage: number;
        description: string;
      };
    }>(response);
  }

  // Promo Codes API
  async getPromoCodes(params: FindAllPromoCodesParams = {}): Promise<PromoCodeListResponse> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });

    const response = await fetch(`${API_BASE_URL}/promo-codes?${searchParams}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<PromoCodeListResponse>(response);
  }

  async getPromoCode(id: number): Promise<PromoCode> {
    const response = await fetch(`${API_BASE_URL}/promo-codes/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<PromoCode>(response);
  }

  async createPromoCode(payload: PromoCodePayload): Promise<PromoCode> {
    const response = await fetch(`${API_BASE_URL}/promo-codes`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<PromoCode>(response);
  }

  async updatePromoCode(id: number, payload: Partial<PromoCodePayload>): Promise<PromoCode> {
    const response = await fetch(`${API_BASE_URL}/promo-codes/${id}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<PromoCode>(response);
  }

  async deletePromoCode(id: number): Promise<PromoCode> {
    const response = await fetch(`${API_BASE_URL}/promo-codes/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<PromoCode>(response);
  }

  // Payment Providers API
  async getPaymentProviders(params: FindAllPaymentProvidersParams = {}): Promise<PaymentProviderListResponse> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });

    const response = await fetch(`${API_BASE_URL}/payment-providers?${searchParams.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<PaymentProviderListResponse>(response);
  }

  async createPaymentProvider(payload: PaymentProviderPayload): Promise<PaymentProvider> {
    const response = await fetch(`${API_BASE_URL}/payment-providers`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<PaymentProvider>(response);
  }

  async updatePaymentProvider(id: number, payload: Partial<PaymentProviderPayload>): Promise<PaymentProvider> {
    const response = await fetch(`${API_BASE_URL}/payment-providers/${id}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return this.handleResponse<PaymentProvider>(response);
  }

  async deletePaymentProvider(id: number): Promise<PaymentProvider> {
    const response = await fetch(`${API_BASE_URL}/payment-providers/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<PaymentProvider>(response);
  }

  async getPaymentMethods(): Promise<PaymentMethod[]> {
    const response = await fetch(`${API_BASE_URL}/payment-methods`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
  
    return this.handleResponse<PaymentMethod[]>(response);
  }  
}

export const apiService = new ApiService();

// Helper function to check if user is authenticated
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('token');
  return !!token;
};

// Helper function to logout
export const logout = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  window.location.href = '/admin';
}; 